import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Add, Menu as MenuIcon, Close } from '@mui/icons-material';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';

import {
    ALL,
    Anchor,
    BOOKMARK,
    ContextObjType,
    FormattedObjectType,
    Inputs,
    ItemType,
    JobsTypes,
    RONE,
    SEARCH,
    TabsType,
} from './types';
import { getAllContextsQuery, getSearchHistory } from 'api/queries/job';
import { MinLogo } from '../../assets/image-assets/images';
import { deleteHtmlFromString } from 'helpers/htmlParser';
import SettingsComponent from './components/Settings';
import { Tooltip, Typography } from '@mui/material';
import { getMonthWithDay } from 'helpers/dates';
import { addFirestoreDocs, auth, onUpdateDoc } from 'utils/config/firebase';
import { SEARCH_HISTORY } from 'utils/config/constants/controllerNames';
import { filterValues, searchJob } from 'helpers/search';
import { SearchHistoryType } from 'store/global/types';
import { FilterTabs } from './components/FilterTabs';
import { useAppSelector } from 'app/hooks';
import { selectIsROneFeatureEnabled } from 'store/featureFlag/featureFlagSlice';
import { Archive } from './components/Archive';
import { SearchResults } from './components/SearchResults';
import ROneModal from 'components/Modals/ROneModal';

export default function Sidebar() {
    const { t } = useTranslation();
    const [state, setState] = useState({
        left: false,
    });
    const [searchedSavedValues, setSearchedSavedValues] = useState<Inputs | null>(null);
    const [searchHistory, setSearchHistory] = useState<SearchHistoryType[]>([]);
    const [searchedValue, setSearchedValue] = useState<FormattedObjectType[]>([]);
    const [isAddBtnHovered, setIsAddBtnHovered] = useState<boolean>(false);
    const [isROneModalOpen, setIsROneModalOpen] = useState<boolean>(false);
    const [modalJobOrderId, setModalJobOrderId] = useState<string | undefined>();
    const [data, setData] = useState<FormattedObjectType[]>([]);
    const [activeTab, setActiveTab] = useState<TabsType>(ALL);

    const savedUnFormattedData = useRef<JobsTypes[]>([]);
    const isROne = useAppSelector(selectIsROneFeatureEnabled);
    const baseWidth = isAddBtnHovered ? 283 : 133;
    const rOneWidth = 'auto';
    const openBtnStyle = isROne ? { marginLeft: 0 } : {};

    useEffect(() => {
        const unsubscribe = getAllContextsQuery(setData, formatData, savedUnFormattedData);
        const unsubscribeSearch = getSearchHistory(setSearchHistory);

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
            if (unsubscribeSearch) {
                unsubscribeSearch();
            }
        };
    }, []);

    useEffect(() => {
        if (searchedSavedValues) {
            onSearch(searchedSavedValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(data)]);

    const onOpenROneModal = (jobOrderId?: string) => {
        setModalJobOrderId(jobOrderId);
        setIsROneModalOpen(true);
    };

    const onCloseROneModal = () => {
        setIsROneModalOpen(false);
    };

    const addSearchHistory = (title: string, subtitle: string, job_id: string) => {
        const existingItem = searchHistory.find((item) => item.job_id === job_id);

        if (existingItem) {
            const data = {
                ...existingItem,
                updated_at: new Date().toISOString(),
            };
            onUpdateDoc(SEARCH_HISTORY, existingItem.document_id, data);
        } else {
            const data = {
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                job_id,
                title,
                subtitle,
                user_id: auth?.currentUser?.uid,
            };
            addFirestoreDocs(SEARCH_HISTORY, data);
        }
    };

    const formatData = (items: JobsTypes[]) => {
        const formattedData = items.reduce((acc, item) => {
            const { month, day, year } = getMonthWithDay(item.created_at);
            const title = deleteHtmlFromString(item.title || item.intake_notes);
            const data: ItemType = {
                month,
                day,
                title: title,
                year,
                subtitle: item.subtitle,
                document_id: item.document_id,
                messages: item.messages,
                isSaved: item.isSaved,
                jobOrderId: item.job_order_id,
            };

            acc[month] = acc[month]
                ? { id: month, year, items: [...acc[month].items, data] }
                : { id: month, year, items: [data] };

            return acc;
        }, {} as ContextObjType);

        return Object.values(formattedData);
    };

    const toggleDrawer =
        (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
            if (
                event.type === 'keydown' &&
                ((event as React.KeyboardEvent).key === 'Tab' ||
                    (event as React.KeyboardEvent).key === 'Shift')
            ) {
                return;
            }

            setState({ ...state, [anchor]: open });
        };

    const onCloseSidebar = (anchor: string) => {
        setState({ ...state, [anchor]: false });
    };

    const closeBar = () => setState((prev) => ({ ...prev, left: false }));

    const handleTab = (type: TabsType) => {
        setActiveTab(type);
    };

    const onSearch = ({ searchValue, isBookmarkSelected, monthFilter }: Inputs) => {
        setSearchedSavedValues({ searchValue, isBookmarkSelected, monthFilter });
        const searchedResults = searchJob({
            data: savedUnFormattedData.current,
            searchValue,
            isBookmarkSelected,
            monthFilter,
        });
        setSearchedValue(formatData(searchedResults));
    };

    const showValue = () => {
        switch (activeTab) {
            case SEARCH:
                return searchedValue;
            case BOOKMARK:
                return formatData(filterValues(BOOKMARK, savedUnFormattedData.current));
            case RONE:
                return formatData(filterValues(RONE, savedUnFormattedData.current));
            case ALL:
                return formatData(filterValues(ALL, savedUnFormattedData.current));
            default:
                return data;
        }
    };

    const showedValue = showValue();

    return (
        <>
            <Fragment key={'left'}>
                <Drawer anchor={'left'} open={state['left']} onClose={toggleDrawer('left', false)}>
                    <Box sx={{ width: 450 }} role="presentation" className="sidebar-open-view">
                        <Box>
                            <Box
                                className="close-bar"
                                onClick={toggleDrawer('left', false)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <button>
                                    <Close sx={{ fontSize: 24 }} />
                                </button>
                                {t('web_app.close_btn')}
                            </Box>
                            <Link to="/" className="logo-button">
                                <img src={MinLogo} alt="Randstad" />
                                {t('web_app.home.left_sidebar.title')}
                            </Link>
                            <FilterTabs handleTab={handleTab} activeTab={activeTab} />
                            {activeTab === SEARCH ? (
                                <SearchResults
                                    activeTab={activeTab}
                                    searchHistory={searchHistory}
                                    searchedSavedValues={searchedSavedValues}
                                    searchedValue={searchedValue}
                                    savedUnFormattedData={savedUnFormattedData}
                                    onSearch={onSearch}
                                    addSearchHistory={addSearchHistory}
                                    closeBar={closeBar}
                                    onOpenROneModal={onOpenROneModal}
                                />
                            ) : (
                                <Archive
                                    activeTab={activeTab}
                                    archiveItems={showedValue}
                                    closeSidebar={closeBar}
                                    onOpenROneModal={onOpenROneModal}
                                />
                            )}
                        </Box>
                        <SettingsComponent onCloseSidebar={() => onCloseSidebar('left')} />
                    </Box>
                </Drawer>
            </Fragment>
            <Box
                className="left-sidebar"
                sx={{
                    minWidth: isROne ? rOneWidth : baseWidth,
                    maxWidth: isAddBtnHovered ? 'none' : baseWidth,
                }}
            >
                <Tooltip placement="bottom" title={t('web_app.tooltips.left_sidebar')}>
                    <button
                        className="left-sidebar-open-menu-btn"
                        onClick={toggleDrawer('left', true)}
                        style={openBtnStyle}
                    >
                        <MenuIcon sx={{ fontSize: 24 }} />
                    </button>
                </Tooltip>
                {isROne ? (
                    <></>
                ) : (
                    <Link
                        onMouseEnter={() => setIsAddBtnHovered(true)}
                        onMouseLeave={() => setIsAddBtnHovered(false)}
                        to="/"
                        className="left-sidebar-add-btn"
                        reloadDocument
                    >
                        <Add className="add-icon" sx={{ fontSize: 16, color: '#FFF' }} />
                        <Typography className="add-tooltip" variant="body2">
                            {t('web_app.home.left_sidebar.add_btn')}
                        </Typography>
                    </Link>
                )}
            </Box>
            <ROneModal
                open={isROneModalOpen}
                jobOrderId={modalJobOrderId}
                onClose={onCloseROneModal}
            />
        </>
    );
}
